<template>
  <div class="pb-4 mt-4 card">
    <div class="px-4 sm:px-6 lg:px-8">
      <div class="flow-root">
        <div class="flex justify-end items-center mt-2">
          <button
            v-if="canMergeChalans"
            class="px-4 py-2 text-white bg-green-500 rounded disabled:opacity-50"
            @click="redirectSelected"
          >
            Merge Chalans To Sale
          </button>
        </div>
        <div class="card-header">
          <span
            class="mb-0 text-lg font-bold"
            style="text-transform: capitalize"
          >
            Customer Chalans
          </span>
        </div>

        <!-- Filter Panel -->
        <div class="flex-shrink-0 px-4 py-2 mb-2 bg-gray-50 rounded-lg border border-gray-200">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <!-- Date Range -->
            <div class="col-span-2 space-y-2">
              <label class="block text-xs font-medium text-gray-600">Date Range</label>
              <div class="flex space-x-2">
                <input
                  v-model="panelFilters.fromDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  v-model="panelFilters.toDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div class="flex flex-wrap gap-1">
                <button type="button" @click="setDateFilter('currentFY')" :class="dateQuickFilterClass('currentFY')">Current FY</button>
                <button type="button" @click="setDateFilter('prevFY')" :class="dateQuickFilterClass('prevFY')">Prev FY</button>
                <button type="button" @click="setDateFilter('today')" :class="dateQuickFilterClass('today')">Today</button>
                <button type="button" @click="setDateFilter('yesterday')" :class="dateQuickFilterClass('yesterday')">Yesterday</button>
                <button type="button" @click="setDateFilter('thisWeek')" :class="dateQuickFilterClass('thisWeek')">This Week</button>
                <button type="button" @click="setDateFilter('thisMonth')" :class="dateQuickFilterClass('thisMonth')">This Month</button>
                <button type="button" @click="setDateFilter('lastMonth')" :class="dateQuickFilterClass('lastMonth')">Last Month</button>
              </div>
            </div>
            <!-- Branch -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Branch</label>
              <select
                v-model="panelFilters.branch"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Branches</option>
                <option v-for="b in allBranches" :key="b.id" :value="b.name">{{ b.name }}</option>
              </select>
            </div>
            <!-- Customer Name -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Customer Name</label>
              <input
                v-model="panelFilters.customerName"
                type="text"
                @keyup.enter="applySingleFilter('customerName')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Chalan Number -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Chalan Number</label>
              <input
                v-model="panelFilters.chalanNumber"
                type="text"
                @keyup.enter="applySingleFilter('chalanNumber')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Type -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Type</label>
              <select
                v-model="panelFilters.type"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="Inward">Inward</option>
                <option value="Outward">Outward</option>
              </select>
            </div>
            <!-- Status -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Status</label>
              <select
                v-model="panelFilters.status"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Dispatched">Dispatched</option>
                <option value="Completed">Completed</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <!-- Actions -->
            <div class="space-y-2">
              <label class="block text-xs font-medium text-gray-600">Actions</label>
              <div class="flex space-x-2">
                <button
                  @click="applyFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                >Apply</button>
                <button
                  @click="clearFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-gray-500 rounded-md transition-colors hover:bg-gray-600"
                >Clear</button>
              </div>
            </div>
          </div>

          <!-- Active Filters -->
          <div v-if="hasActiveFilters" class="pt-4 mt-4 border-t border-gray-200">
            <div class="flex flex-wrap gap-2 items-center">
              <span class="text-xs font-medium text-gray-600">Active Filters:</span>
              <span
                v-if="appliedFilters.fromDate || appliedFilters.toDate"
                class="inline-flex items-center px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full"
              >
                Date: {{ formatAppliedDateRange() }}
                <button @click="clearDateFilter" class="ml-1 text-blue-600 hover:text-blue-800">×</button>
              </span>
              <span
                v-if="appliedFilters.branch"
                class="inline-flex items-center px-2 py-1 text-xs text-purple-800 bg-purple-100 rounded-full"
              >
                Branch: {{ appliedFilters.branch }}
                <button @click="clearAppliedFilter('branch')" class="ml-1 text-purple-600 hover:text-purple-800">×</button>
              </span>
              <span
                v-if="appliedFilters.customerName"
                class="inline-flex items-center px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full"
              >
                Customer: {{ appliedFilters.customerName }}
                <button @click="clearAppliedFilter('customerName')" class="ml-1 text-green-600 hover:text-green-800">×</button>
              </span>
              <span
                v-if="appliedFilters.chalanNumber"
                class="inline-flex items-center px-2 py-1 text-xs text-orange-800 bg-orange-100 rounded-full"
              >
                Chalan: {{ appliedFilters.chalanNumber }}
                <button @click="clearAppliedFilter('chalanNumber')" class="ml-1 text-orange-600 hover:text-orange-800">×</button>
              </span>
              <span
                v-if="appliedFilters.type"
                class="inline-flex items-center px-2 py-1 text-xs text-indigo-800 bg-indigo-100 rounded-full"
              >
                Type: {{ appliedFilters.type }}
                <button @click="clearAppliedFilter('type')" class="ml-1 text-indigo-600 hover:text-indigo-800">×</button>
              </span>
              <span
                v-if="appliedFilters.status"
                class="inline-flex items-center px-2 py-1 text-xs text-red-800 bg-red-100 rounded-full"
              >
                Status: {{ appliedFilters.status }}
                <button @click="clearAppliedFilter('status')" class="ml-1 text-red-600 hover:text-red-800">×</button>
              </span>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div
            class="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8"
          >
            <table class="min-w-full divide-y divide-gray-300">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-4 py-3.5">
                    <input
                      type="checkbox"
                      v-model="selectAll"
                      @change="toggleSelectAll"
                    />
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-0"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-0"
                  >
                    Branch
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-0"
                  >
                    Customer
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-0"
                  >
                    Number
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-0"
                  >
                    Total
                  </th>
                  <th
                    scope="col"
                    class="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-0"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-sm font-semibold text-left text-gray-900"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-sm font-semibold text-left text-gray-900"
                  >
                    Sale Invoice
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-sm font-semibold text-left text-gray-900"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <template v-if="loading">
                  <tr class="text-center">
                    <td colspan="10" class="px-6 py-4">
                      <div class="flex justify-center items-center">
                        <div
                          class="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"
                        ></div>
                        <span class="ml-2">Loading...</span>
                      </div>
                    </td>
                  </tr>
                </template>
                <template v-else-if="customerChalans.length === 0">
                  <tr class="text-center">
                    <td colspan="10" class="px-6 py-4 text-gray-500">
                      No customer chalans found
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="item in customerChalans" :key="item.id">
                    <!-- Checkbox per row -->
                    <td class="px-4">
                      <input
                        type="checkbox"
                        :value="item.id"
                        v-model="selectedItems"
                      />
                    </td>
                    <td
                      class="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-0"
                    >
                      {{ item.date | convertDate }}
                    </td>
                    <td
                      class="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-0"
                    >
                      <span
                        v-if="item.branch"
                        class="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full"
                        :style="branchBadgeStyle(item.branch)"
                        >{{ item.branch }}</span
                      >
                    </td>
                    <td
                      class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                    >
                      {{ item.user }}
                    </td>
                    <td
                      class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                    >
                      {{ item.chalan_number }}
                    </td>
                    <td
                      class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                    >
                      {{ item.total }}
                    </td>
                    <td
                      class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                    >
                      <span
                        :class="{
                          'bg-red-500': item.type === 'Inward',
                          'bg-green-500': item.type === 'Outward'
                        }"
                        class="px-2 py-1 text-xs font-medium text-white rounded-full"
                      >
                        {{ item.type }}
                      </span>
                    </td>
                    <td
                      class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                    >
                      <span
                        :class="{
                          'bg-red-500':
                            item.status === 'Cancelled' ||
                            item.status === 'Pending',
                          'bg-yellow-500': item.status === 'Dispatched',
                          'bg-green-500':
                            item.status === 'Completed' ||
                            item.status === 'Delivered'
                        }"
                        class="px-2 py-1 text-xs font-medium text-white rounded-full"
                      >
                        {{ item.status }}
                      </span>
                    </td>
                    <td class="px-3 py-4 text-sm whitespace-nowrap">
                      <button
                        v-if="item.sale_id"
                        @click="openInvoicePrint(item.sale_id)"
                        class="text-blue-600 hover:underline text-xs font-medium"
                      >View Invoice</button>
                      <span v-else class="text-gray-400 text-xs">—</span>
                    </td>
                    <td
                      class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap"
                    >
                      <div class="inline-block relative text-left">
                        <div>
                          <button
                            type="button"
                            class="inline-flex gap-x-1.5 justify-center px-3 py-2 w-full text-sm font-semibold text-gray-900 bg-blue-300 rounded-md ring-1 ring-inset ring-gray-300 shadow-sm hover:bg-blue-200"
                            id="menu-button"
                            aria-expanded="true"
                            aria-haspopup="true"
                            @click="handleDropdown(item)"
                          >
                            Actions
                          </button>
                        </div>
                        <div
                          v-if="item.isDropdownOpen === true"
                          class="absolute right-0 z-10 mt-2 w-56 bg-white rounded-md ring-1 shadow-lg origin-top-right ring-black/5 focus:outline-none"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="menu-button"
                          tabindex="-1"
                        >
                          <div class="py-1" role="none">
                            <span
                              @click="handlePrint(item)"
                              class="block px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
                              role="menuitem"
                              tabindex="-1"
                              id="menu-item-0"
                              >Print</span
                            >
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div class="flex justify-between items-center px-4 mt-4">
            <!-- Records Per Page Selector -->
            <div class="flex items-center space-x-2">
              <label for="per-page" class="text-sm font-medium"
                >Records per page:</label
              >
              <select
                id="per-page"
                v-model="perPage"
                @change="fetchChalans()"
                class="px-2 py-1 rounded border"
              >
                <option
                  v-for="option in perPageOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </div>

            <!-- Pagination Info -->
            <span v-if="pagination.total > 0" class="text-sm text-gray-600">
              Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} entries
            </span>
            <span v-else class="text-sm text-gray-600">No entries</span>

            <Pagination
              :currentPage="pagination.current_page"
              :totalPages="pagination.last_page"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <el-dialog
      @open="onDialogOpen"
      @close="onDialogClose"
      :visible.sync="showInvoiceModal"
      width="60%"
      title="Invoice"
    >
      <whatsapp-bill-print ref="saleInvoice" />
    </el-dialog>
  </div>
</template>
<script>
import { format } from "date-fns";
import { mapActions } from "vuex";
import { Dialog } from "element-ui";
import Pagination from "../../components/Pagination.vue";
import WhatsAppBillPrint from "./WhatsAppBillPrint.vue";
import { apiHelper } from "../../utils/helpers";
import endpoints from "@/utils/constants";
import { print } from "@/utils/print";
import {
  indianFinancialYearRange,
  dateRangeForPreset,
  matchingDatePreset
} from "@/utils/dateRangeFilters";

export default {
  data() {
    const defaultFY = indianFinancialYearRange("current");
    return {
      customerChalans: [],
      allBranches: [],
      pagination: {
        current_page: 1,
        prev_page_url: null,
        next_page_url: null,
        last_page: 1,
        from: 0,
        to: 0,
        total: 0
      },
      perPage: 20,
      perPageOptions: [5, 10, 25, 50],
      paginationLinks: [],
      selectedItems: [],
      selectAll: false,
      panelFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        branch: "",
        customerName: "",
        chalanNumber: "",
        type: "",
        status: ""
      },
      appliedFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        branch: "",
        customerName: "",
        chalanNumber: "",
        type: "",
        status: ""
      },
      _skipFilterWatchFetch: false,
      loading: false,
      showInvoiceModal: false
    };
  },
  computed: {
    canMergeChalans() {
      if (this.selectedItems.length === 0) {
        return false;
      }

      const selectedChalans = this.customerChalans.filter(chalan =>
        this.selectedItems.includes(chalan.id)
      );

      const allDispatched = selectedChalans.every(
        chalan => chalan.status.toLowerCase() === "dispatched"
      );

      if (!allDispatched) {
        return false;
      }

      const customers = [
        ...new Set(selectedChalans.map(chalan => chalan.userId))
      ];
      const sameCustomer = customers.length === 1;

      return sameCustomer;
    },
    hasActiveFilters() {
      return Object.values(this.appliedFilters).some(v => v !== "");
    },
    activeDatePreset() {
      return matchingDatePreset(this.panelFilters.fromDate, this.panelFilters.toDate);
    }
  },
  filters: {
    convertDate(date) {
      return format(date, "DD MMM YYYY");
    }
  },
  watch: {
    "panelFilters.fromDate"(newVal) {
      this.appliedFilters.fromDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    },
    "panelFilters.toDate"(newVal) {
      this.appliedFilters.toDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    },
    "panelFilters.branch"(newVal) {
      this.appliedFilters.branch = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    },
    "panelFilters.type"(newVal) {
      this.appliedFilters.type = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    },
    "panelFilters.status"(newVal) {
      this.appliedFilters.status = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    }
  },
  components: {
    Pagination,
    [Dialog.name]: Dialog,
    "whatsapp-bill-print": WhatsAppBillPrint
  },
  mounted() {
    this.fetchBranches();
    this.fetchChalans();
  },

  methods: {
    ...mapActions({
      getReceiptDataForWhatsapp: "sale/getReceiptDataForWhatsapp"
    }),
    openInvoicePrint(saleId) {
      this.getReceiptDataForWhatsapp(saleId).then(status => {
        if (status === 200) {
          this.showInvoiceModal = true;
        }
      });
    },
    onDialogOpen() {
      if (this.$refs && this.$refs.saleInvoice) {
        this.$refs.saleInvoice.fetchData();
      }
    },
    onDialogClose() {
      if (this.$refs && this.$refs.saleInvoice) {
        this.$refs.saleInvoice.resetProperty();
      }
    },
    fetchBranches() {
      apiHelper
        .call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`)
        .then((res) => { this.allBranches = res.data.data || res.data; })
        .catch(() => {});
    },
    branchBadgeStyle(branchName) {
      const b = this.allBranches.find((x) => x.name === branchName);
      if (!b || !b.color) return { background: '#dbeafe', color: '#1e40af' };
      return { background: b.color, color: '#fff' };
    },
    buildQueryUrl(page = 1) {
      const params = [`page=${page}`, `per_page=${this.perPage}`];
      if (this.appliedFilters.fromDate) params.push(`from_date=${this.appliedFilters.fromDate}`);
      if (this.appliedFilters.toDate) params.push(`to_date=${this.appliedFilters.toDate}`);
      if (this.appliedFilters.branch) params.push(`branch=${encodeURIComponent(this.appliedFilters.branch)}`);
      if (this.appliedFilters.customerName) params.push(`customer_name=${encodeURIComponent(this.appliedFilters.customerName)}`);
      if (this.appliedFilters.chalanNumber) params.push(`chalan_number=${encodeURIComponent(this.appliedFilters.chalanNumber)}`);
      if (this.appliedFilters.type) {
        const isInward = this.appliedFilters.type === 'Inward' ? 1 : 0;
        params.push(`is_inward=${isInward}`);
      }
      if (this.appliedFilters.status) params.push(`status=${encodeURIComponent(this.appliedFilters.status)}`);
      return `/api/v1/customer-chalans?${params.join("&")}`;
    },
    fetchChalans(page = 1) {
      this.loading = true;
      apiHelper
        .call("get", `${endpoints.LARAVEL_BASE_URL}${this.buildQueryUrl(page)}`)
        .then(response => {
          this.customerChalans = response.data.data.map(i => ({ ...i, isDropdownOpen: false }));
          this.pagination = {
            current_page: response.data.meta.current_page,
            prev_page_url: response.data.meta.prev_page_url,
            next_page_url: response.data.meta.next_page_url,
            last_page: response.data.meta.last_page,
            from: response.data.meta.from || 0,
            to: response.data.meta.to || 0,
            total: response.data.meta.total || 0
          };
          this.loading = false;
        })
        .catch(error => {
          console.error("Error fetching customer chalans:", error);
          this.$notify({
            message: "Error fetching customer chalans",
            type: "danger",
            title: "Error"
          });
          this.loading = false;
        });
    },
    handlePageChange(page) {
      this.fetchChalans(page);
    },
    applyFilters() {
      this.appliedFilters = { ...this.panelFilters };
      this.fetchChalans(1);
    },
    applySingleFilter(filterName) {
      this.appliedFilters[filterName] = this.panelFilters[filterName];
      this.fetchChalans(1);
    },
    clearAppliedFilter(filterName) {
      this.appliedFilters[filterName] = "";
      this.panelFilters[filterName] = "";
      this.fetchChalans(1);
    },
    clearDateFilter() {
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = "";
      this.panelFilters.toDate = "";
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = "";
        this.appliedFilters.toDate = "";
        this.fetchChalans(1);
      });
    },
    clearFilters() {
      this._skipFilterWatchFetch = true;
      const empty = {
        fromDate: "", toDate: "", branch: "", customerName: "",
        chalanNumber: "", type: "", status: ""
      };
      this.panelFilters = { ...empty };
      this.appliedFilters = { ...empty };
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.fetchChalans(1);
      });
    },
    setDateFilter(type) {
      const range = dateRangeForPreset(type);
      if (!range) return;
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = range.fromDate;
      this.panelFilters.toDate = range.toDate;
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = range.fromDate;
        this.appliedFilters.toDate = range.toDate;
        this.fetchChalans(1);
      });
    },
    dateQuickFilterClass(preset) {
      const selected = this.activeDatePreset === preset;
      return [
        "px-2 py-1 text-xs rounded transition-colors",
        selected
          ? "text-blue-700 bg-blue-100 hover:bg-blue-200"
          : "text-gray-700 bg-gray-100 hover:bg-gray-200"
      ];
    },
    formatAppliedDateRange() {
      const f = this.appliedFilters.fromDate;
      const t = this.appliedFilters.toDate;
      if (f && t) {
        if (f === t) return new Date(f).toLocaleDateString("en-IN");
        return `${new Date(f).toLocaleDateString("en-IN")} - ${new Date(t).toLocaleDateString("en-IN")}`;
      }
      if (f) return `From ${new Date(f).toLocaleDateString("en-IN")}`;
      if (t) return `Until ${new Date(t).toLocaleDateString("en-IN")}`;
      return "";
    },
    handleDropdown(item) {
      const data = this.customerChalans.map(i => {
        if (i.id === item.id) {
          return { ...i, isDropdownOpen: item.isDropdownOpen === true ? false : true };
        } else {
          return { ...i, isDropdownOpen: false };
        }
      });
      this.customerChalans = data;
    },
    async handlePrint(item) {
      try {
        this.handleDropdown(item);

        const response = await apiHelper.call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/customer-chalans/${item.id}`
        );

        const chalanData = response.data;

        const printData = {
          products: chalanData.products || [],
          user: chalanData.user || {},
          chalanNumber: chalanData.chalan_number || chalanData.id,
          is_inward: chalanData.is_inward || false,
          type: chalanData.type || (chalanData.is_inward ? "Inward" : "Outward"),
          status: chalanData.status || "Unknown",
          branch: chalanData.branch || "Unknown",
          subTotal: chalanData.sub_total || 0,
          grandTotal: chalanData.grand_total || 0,
          roundedOff: chalanData.rounded_off || 0,
          createdAt: chalanData.created_at || chalanData.date,
          updatedAt: chalanData.updated_at || chalanData.date
        };

        print(printData, "customer-chalan");

        this.$notify({
          message: "Chalan sent to printer!",
          type: "success",
          title: "Success"
        });
      } catch (error) {
        console.error("Error printing chalan:", error);
        this.$notify({
          message: "Error printing chalan. Please try again.",
          type: "danger",
          title: "Error"
        });
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.customerChalans.map(item => item.id);
      } else {
        this.selectedItems = [];
      }
    },
    redirectSelected() {
      const selectedCustomerChalans = this.customerChalans
        .filter(chalan => this.selectedItems.includes(chalan.id))
        .map(chalan => ({
          id: chalan.id,
          userId: chalan.userId,
          status: chalan.status
        }));
      window.sessionStorage.removeItem("customerChalansToMerge");
      window.sessionStorage.setItem(
        "customerChalansToMerge",
        JSON.stringify(selectedCustomerChalans)
      );
      this.$router.push({ name: "merge-customer-chalans" });
    }
  }
};
</script>
